import React, { useEffect, useMemo, useRef, useState } from 'react';
import api from '../lib/api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import './CSS/StockPremium.css';

const STOCK_FUEL_OPTIONS = ['Petrol', 'Diesel', 'Power Petrol', 'Power Diesel', 'CNG', 'Diesel 100'];
const HPCL_STANDARD_CAPACITIES = [9000, 16000, 22000, 35000, 45000, 70000];

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function getFuelTone(fuelType) {
  const fuel = String(fuelType || '').toLowerCase();
  if (fuel.includes('diesel')) return 'diesel';
  if (fuel.includes('cng')) return 'cng';
  if (fuel.includes('power')) return 'power';
  return 'petrol';
}

function toDateTimeLocal(value) {
  if (!value) return '';
  const raw = String(value).replace(' ', 'T');
  return raw.slice(0, 16);
}

function todayDateTime() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

function todayDate() {
  return todayDateTime().slice(0, 10);
}

function monthStart() {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`;
}

function monthEnd() {
  const now = new Date();
  const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const pad = (n) => String(n).padStart(2, '0');
  return `${last.getFullYear()}-${pad(last.getMonth() + 1)}-${pad(last.getDate())}`;
}

function combineDateAndTime(dateValue, timeValue) {
  if (!dateValue) return todayDateTime();
  const safeTime = String(timeValue || '').trim() || '07:00';
  return `${dateValue}T${safeTime.slice(0, 5)}`;
}

function defaultTimeForShift(shift) {
  return String(shift || '').toLowerCase() === 'night' ? '19:00' : '07:00';
}

function startCaseShift(shift) {
  return String(shift || '').toLowerCase() === 'night' ? 'Night' : 'Morning';
}

function parseDateOnly(value) {
  if (!value) return null;
  const [year, month, day] = String(value).slice(0, 10).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function dayDiff(fromDate, toDate) {
  const from = parseDateOnly(fromDate);
  const to = parseDateOnly(toDate);
  if (!from || !to) return null;
  const diffMs = to.setHours(0, 0, 0, 0) - from.setHours(0, 0, 0, 0);
  return Math.round(diffMs / 86400000);
}

function money(value) {
  return `Rs ${Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function qty(value) {
  return Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

function wholeQty(value) {
  return Number(Math.round(Number(value || 0))).toLocaleString('en-IN');
}

function normalizeFuelKey(value) {
  return String(value || '').trim().toLowerCase();
}

function getFuelChartColor(fuelType) {
  const tone = getFuelTone(fuelType);
  if (tone === 'diesel') return '#16a34a';
  if (tone === 'cng') return '#0f766e';
  if (tone === 'power') return '#f97316';
  return '#2563eb';
}

export default function Stock() {
  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [meta, setMeta] = useState({ pumps: [], fuelTypes: [], tanks: [] });
  const [tanks, setTanks] = useState([]);
  const [dipEntries, setDipEntries] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [manualTotalizerEntries, setManualTotalizerEntries] = useState([]);
  const [totalizer, setTotalizer] = useState({ columns: [], rows: [] });
  const [dsrRows, setDsrRows] = useState([]);
  const [dipChartRows, setDipChartRows] = useState([]);
  const [dipPreview, setDipPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingTankId, setEditingTankId] = useState(null);
  const [dsrTankId, setDsrTankId] = useState('');
  const [mappingBusyId, setMappingBusyId] = useState(null);
  const [showMappingBoard, setShowMappingBoard] = useState(true);
  const [draggingNozzleId, setDraggingNozzleId] = useState('');
  const [dragPointer, setDragPointer] = useState(null);
  const [mappingLines, setMappingLines] = useState([]);

  const mappingBoardRef = useRef(null);
  const nozzleNodeRefs = useRef({});
  const tankNodeRefs = useRef({});
  const nozzleListRef = useRef(null);
  const tankListRef = useRef(null);

  const [tankForm, setTankForm] = useState({
    tank_name: '',
    fuel_type: 'Petrol',
    tank_capacity: '',
    current_volume: '',
    diameter_cm: '',
    length_cm: '',
    dead_stock_litres: '',
    is_active: true,
  });

  const [dipForm, setDipForm] = useState({
    tank_id: '',
    recorded_at: todayDateTime(),
    shift: '',
    entry_type: 'closing',
    dip_cm: '',
    water_cm: '',
    remarks: '',
  });

  const [deliveryForm, setDeliveryForm] = useState({
    tank_id: '',
    delivery_date: todayDateTime(),
    invoice_no: '',
    supplier_name: '',
    receipt_litres: '',
    remarks: '',
  });

  const [manualTotalizerForm, setManualTotalizerForm] = useState({
    nozzle_id: '',
    tank_id: '',
    entry_datetime: todayDateTime(),
    opening_totalizer: '',
    closing_totalizer: '',
    testing_litres: '',
    rate: '',
    remarks: '',
  });

  const [filters, setFilters] = useState({
    tank_id: '',
    startDate: monthStart(),
    endDate: monthEnd(),
  });

  const [showGuidedEntry, setShowGuidedEntry] = useState(false);
  const [guidedEntryDismissed, setGuidedEntryDismissed] = useState(false);
  const [guidedStep, setGuidedStep] = useState(0);
  const [guidedSaving, setGuidedSaving] = useState(false);
  const [guidedContext, setGuidedContext] = useState({
    tank_id: '',
    entry_date: todayDate(),
    shift: 'morning',
  });
  const [guidedDipForm, setGuidedDipForm] = useState({
    dip_cm: '',
    water_cm: '',
    remarks: '',
  });
  const [guidedReceiptChoice, setGuidedReceiptChoice] = useState('');
  const [guidedReceiptForm, setGuidedReceiptForm] = useState({
    time: '09:00',
    invoice_no: '',
    supplier_name: '',
    receipt_litres: '',
    remarks: '',
  });
  const [guidedTotalizerTime, setGuidedTotalizerTime] = useState('21:00');
  const [guidedTotalizerRows, setGuidedTotalizerRows] = useState([]);

  const availableFuelTypes = useMemo(() => {
    const merged = [...STOCK_FUEL_OPTIONS, ...(meta.fuelTypes || [])]
      .map((item) => String(item || '').trim())
      .filter(Boolean);
    return Array.from(new Set(merged));
  }, [meta.fuelTypes]);

  const fetchMeta = async () => {
    const res = await api.get('/api/fuel/meta', { headers });
    setMeta(res.data || { pumps: [], fuelTypes: [], tanks: [] });
  };

  const fetchTanks = async () => {
    const res = await api.get('/api/fuel', { headers });
    setTanks(res.data || []);
  };

  const fetchDipEntries = async () => {
    const res = await api.get('/api/fuel/dips', {
      headers,
      params: {
        tank_id: filters.tank_id || undefined,
        startDate: `${filters.startDate}T00:00`,
        endDate: `${filters.endDate}T23:59`,
      },
    });
    setDipEntries(res.data || []);
  };

  const fetchDeliveries = async () => {
    const res = await api.get('/api/fuel/deliveries', {
      headers,
      params: {
        tank_id: filters.tank_id || undefined,
        startDate: `${filters.startDate}T00:00`,
        endDate: `${filters.endDate}T23:59`,
      },
    });
    setDeliveries(res.data || []);
  };

  const fetchTotalizer = async () => {
    const res = await api.get('/api/fuel/totalizer', {
      headers,
      params: {
        tank_id: filters.tank_id || undefined,
        startDate: `${filters.startDate}T00:00`,
        endDate: `${filters.endDate}T23:59`,
      },
    });
    setTotalizer(res.data || { columns: [], rows: [] });
  };

  const fetchManualTotalizerEntries = async () => {
    const res = await api.get('/api/fuel/manual-totalizer', {
      headers,
      params: {
        tank_id: filters.tank_id || undefined,
        startDate: `${filters.startDate}T00:00`,
        endDate: `${filters.endDate}T23:59`,
      },
    });
    setManualTotalizerEntries(res.data || []);
  };

  const fetchDsr = async () => {
    const res = await api.get('/api/fuel/dsr', {
      headers,
      params: {
        tank_id: dsrTankId || undefined,
        startDate: filters.startDate,
        endDate: filters.endDate,
      },
    });
    setDsrRows(res.data || []);
  };

  const fetchAll = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchMeta(),
        fetchTanks(),
        fetchDipEntries(),
        fetchDeliveries(),
        fetchManualTotalizerEntries(),
        fetchTotalizer(),
        fetchDsr(),
      ]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    fetchDipEntries();
    fetchDeliveries();
    fetchManualTotalizerEntries();
    fetchTotalizer();
    fetchDsr();
  }, [filters.tank_id, filters.startDate, filters.endDate, dsrTankId]);

  useEffect(() => {
    if (!dipForm.tank_id || dipForm.dip_cm === '' || dipForm.dip_cm === null || dipForm.dip_cm === undefined) {
      setDipPreview(null);
      return;
    }

    api
      .get(`/api/fuel/dip-preview/${dipForm.tank_id}`, {
        headers,
        params: { dip_cm: dipForm.dip_cm },
      })
      .then((res) => setDipPreview(res.data || null))
      .catch(() => setDipPreview(null));
  }, [dipForm.tank_id, dipForm.dip_cm, headers]);

  const selectedTank = useMemo(
    () => tanks.find((row) => String(row.id) === String(filters.tank_id || dipForm.tank_id || deliveryForm.tank_id)),
    [tanks, filters.tank_id, dipForm.tank_id, deliveryForm.tank_id]
  );

  const focusTank = useMemo(() => selectedTank || tanks[0] || null, [selectedTank, tanks]);

  const summary = useMemo(() => {
    const theoretical = tanks.reduce((sum, row) => sum + Number(row.current_theoretical_litres || 0), 0);
    const physical = tanks.reduce((sum, row) => sum + Number(row.last_physical_litres || 0), 0);
    const variance = tanks.reduce((sum, row) => sum + Number(row.variance_litres || 0), 0);
    const missingDip = tanks.filter((row) => String(row.last_dip_at || '').slice(0, 10) !== todayDate()).length;
    return { theoretical, physical, variance, missingDip };
  }, [tanks]);

  const setupAlerts = useMemo(() => {
    const missingGeometry = tanks
      .filter((row) => {
        const capacity = Number(row.tank_capacity || 0);
        const hasStandardChart = HPCL_STANDARD_CAPACITIES.some((item) => Math.abs(item - capacity) <= 1);
        return !hasStandardChart && (Number(row.diameter_cm || 0) <= 0 || Number(row.length_cm || 0) <= 0);
      })
      .map((row) => row.tank_name || row.fuel_type || `Tank ${row.id}`);
    return {
      missingGeometry,
    };
  }, [tanks]);

  const dipChartSeries = useMemo(
    () =>
      (dipChartRows || []).map((row) => ({
        dip: Number(row.dip_cm || 0),
        physical: Number(row.physical_volume_litres || 0),
        diff: Number(row.diff_litres_per_mm || 0),
      })),
    [dipChartRows]
  );

  const dipChartColor = useMemo(
    () => getFuelChartColor(selectedTank?.fuel_type || focusTank?.fuel_type),
    [selectedTank, focusTank]
  );

  const nozzleMappingBoard = useMemo(
    () =>
      tanks.map((tank) => {
        const fuelKey = normalizeFuelKey(tank.fuel_type);
        const mappedNozzles = (meta.nozzles || []).filter(
          (nozzle) => String(nozzle.tank_id || '') === String(tank.id)
        );
        const candidateNozzles = (meta.nozzles || []).filter(
          (nozzle) => normalizeFuelKey(nozzle.fuel_type) === fuelKey
        );
        return {
          ...tank,
          mappedNozzles,
          candidateNozzles,
        };
      }),
    [tanks, meta.nozzles]
  );

  const mappingNozzles = useMemo(
    () =>
      [...(meta.nozzles || [])].sort((a, b) => {
        const fuelCompare = String(a.fuel_type || '').localeCompare(String(b.fuel_type || ''));
        if (fuelCompare !== 0) return fuelCompare;
        return String(a.name || '').localeCompare(String(b.name || ''));
      }),
    [meta.nozzles]
  );

  const mappingSummary = useMemo(() => {
    const mappedCount = mappingNozzles.filter((nozzle) => nozzle.tank_id).length;
    const allNozzlesMapped = mappingNozzles.length > 0 && mappedCount === mappingNozzles.length;
    const allTanksCovered =
      tanks.length > 0 &&
      tanks.every((tank) => mappingNozzles.some((nozzle) => String(nozzle.tank_id || '') === String(tank.id)));

    return {
      mappedCount,
      totalNozzles: mappingNozzles.length,
      coveredTanks: tanks.filter((tank) =>
        mappingNozzles.some((nozzle) => String(nozzle.tank_id || '') === String(tank.id))
      ).length,
      totalTanks: tanks.length,
      completed: allNozzlesMapped && allTanksCovered,
    };
  }, [mappingNozzles, tanks]);

  const draggedNozzle = useMemo(
    () => mappingNozzles.find((nozzle) => String(nozzle.id) === String(draggingNozzleId)) || null,
    [mappingNozzles, draggingNozzleId]
  );

  const dragPreviewLine = useMemo(() => {
    if (!showMappingBoard || !draggedNozzle || !dragPointer || !mappingBoardRef.current) return null;
    const nozzleNode = nozzleNodeRefs.current[draggedNozzle.id];
    if (!nozzleNode) return null;
    const boardRect = mappingBoardRef.current.getBoundingClientRect();
    const nozzleRect = nozzleNode.getBoundingClientRect();
    return {
      x1: nozzleRect.right - boardRect.left,
      y1: nozzleRect.top + nozzleRect.height / 2 - boardRect.top,
      x2: dragPointer.x,
      y2: dragPointer.y,
      color: getFuelChartColor(draggedNozzle.fuel_type),
    };
  }, [showMappingBoard, draggedNozzle, dragPointer]);

  const selectedDsrTank = useMemo(
    () => tanks.find((row) => String(row.id) === String(dsrTankId)),
    [tanks, dsrTankId]
  );

  useEffect(() => {
    if (!tanks.length) {
      setDsrTankId('');
      return;
    }
    if (!dsrTankId || !tanks.some((row) => String(row.id) === String(dsrTankId))) {
      setDsrTankId(String(tanks[0].id));
    }
  }, [tanks, dsrTankId]);

  useEffect(() => {
    if (!mappingSummary.completed) {
      setShowMappingBoard(true);
      return;
    }
    setShowMappingBoard(false);
  }, [mappingSummary.completed]);

  const exportDsrExcel = () => {
    if (!dsrRows.length) return;

    const exportData = dsrRows.map((row) => ({
      Date: row.date || '',
      Tank: row.tank_name || '',
      FuelType: row.fuel_type || '',
      OpeningStock: Number(row.opening_stock || 0),
      Receipt: Number(row.receipt || 0),
      TotalStock: Number(row.total_stock || 0),
      SalesByMeter: Number(row.sales_by_meter || 0),
      PumpTest: Number(row.pump_test || 0),
      NetSalesByMeter: Number(row.net_sales_by_meter || 0),
      CumulativeSales: Number(row.cumulative_sales || 0),
      SalesByDip: row.sales_by_dip == null ? '' : Number(row.sales_by_dip),
      DailyVariation: row.variation_daily == null ? '' : Number(row.variation_daily),
      CumulativeVariation: row.cumulative_variation == null ? '' : Number(row.cumulative_variation),
      ClosingStock: row.closing_stock == null ? '' : Number(row.closing_stock),
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'DSR');
    const buffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
    const tankToken = (selectedDsrTank?.tank_name || selectedDsrTank?.fuel_type || 'all_tanks')
      .replace(/[^a-z0-9]+/gi, '_')
      .replace(/^_+|_+$/g, '');
    saveAs(new Blob([buffer]), `dsr_${tankToken || 'all_tanks'}_${filters.startDate}_${filters.endDate}.xlsx`);
  };

  const exportDsrPdf = () => {
    if (!dsrRows.length) return;

    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    const titleTank = selectedDsrTank?.tank_name || selectedDsrTank?.fuel_type || 'All Tanks';

    doc.setFontSize(15);
    doc.setFont('helvetica', 'bold');
    doc.text('DSR Report', 14, 14);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Tank: ${titleTank}`, 14, 21);
    doc.text(`Date Range: ${filters.startDate} to ${filters.endDate}`, 14, 27);

    autoTable(doc, {
      startY: 32,
      head: [[
        'Date',
        'Tank',
        'Fuel',
        'Opening',
        'Receipt',
        'Total',
        'Sales By Meter',
        'Pump Test',
        'Net Sales',
        'Cumulative Sales',
        'Sales By Dip',
        'Daily Variation',
        'Cumulative Variation',
        'Closing',
      ]],
      body: dsrRows.map((row) => ([
        row.date || '-',
        row.tank_name || '-',
        row.fuel_type || '-',
        qty(row.opening_stock),
        qty(row.receipt),
        qty(row.total_stock),
        qty(row.sales_by_meter),
        qty(row.pump_test),
        qty(row.net_sales_by_meter),
        qty(row.cumulative_sales),
        row.sales_by_dip == null ? '-' : qty(row.sales_by_dip),
        row.variation_daily == null ? '-' : qty(row.variation_daily),
        row.cumulative_variation == null ? '-' : qty(row.cumulative_variation),
        row.closing_stock == null ? '-' : qty(row.closing_stock),
      ])),
      styles: { fontSize: 7, cellPadding: 2 },
      headStyles: {
        fillColor: [23, 32, 51],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 8, right: 8 },
    });

    const tankToken = titleTank
      .replace(/[^a-z0-9]+/gi, '_')
      .replace(/^_+|_+$/g, '');
    doc.save(`dsr_${tankToken || 'all_tanks'}_${filters.startDate}_${filters.endDate}.pdf`);
  };

  useEffect(() => {
    const previewTankId = selectedTank?.id || dipForm.tank_id;
    if (!previewTankId) {
      setDipChartRows([]);
      return;
    }
    api
      .get(`/api/fuel/dip-chart/${previewTankId}`, { headers })
      .then((res) => setDipChartRows(res.data?.rows || []))
      .catch(() => setDipChartRows([]));
  }, [selectedTank?.id, dipForm.tank_id, headers]);

  useEffect(() => {
    if (!showMappingBoard) {
      setMappingLines([]);
      return;
    }

    const computeLines = () => {
      const boardNode = mappingBoardRef.current;
      if (!boardNode) return;
      const boardRect = boardNode.getBoundingClientRect();
      const nextLines = mappingNozzles
        .filter((nozzle) => nozzle.tank_id)
        .map((nozzle) => {
          const nozzleNode = nozzleNodeRefs.current[nozzle.id];
          const tankNode = tankNodeRefs.current[nozzle.tank_id];
          if (!nozzleNode || !tankNode) return null;
          const nozzleRect = nozzleNode.getBoundingClientRect();
          const tankRect = tankNode.getBoundingClientRect();
          return {
            id: `${nozzle.id}-${nozzle.tank_id}`,
            x1: nozzleRect.right - boardRect.left,
            y1: nozzleRect.top + nozzleRect.height / 2 - boardRect.top,
            x2: tankRect.left - boardRect.left,
            y2: tankRect.top + tankRect.height / 2 - boardRect.top,
            color: getFuelChartColor(nozzle.fuel_type),
          };
        })
        .filter(Boolean);
      setMappingLines(nextLines);
    };

    const rafId = window.requestAnimationFrame(computeLines);
    window.addEventListener('resize', computeLines);
    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', computeLines);
    };
  }, [showMappingBoard, mappingNozzles, tanks]);

  const selectedManualTank = useMemo(
    () => tanks.find((row) => String(row.id) === String(manualTotalizerForm.tank_id)),
    [tanks, manualTotalizerForm.tank_id]
  );

  const manualTankNozzles = useMemo(() => {
    if (!manualTotalizerForm.tank_id) return [];
    return (meta.nozzles || []).filter(
      (row) => String(row.tank_id || '') === String(manualTotalizerForm.tank_id)
    );
  }, [meta.nozzles, manualTotalizerForm.tank_id]);

  const selectedManualNozzle = useMemo(
    () => (meta.nozzles || []).find((row) => String(row.id) === String(manualTotalizerForm.nozzle_id)),
    [meta.nozzles, manualTotalizerForm.nozzle_id]
  );

  const manualAvailableTanks = useMemo(
    () =>
      [...tanks].sort((a, b) =>
        String(a.tank_name || a.fuel_type || '').localeCompare(String(b.tank_name || b.fuel_type || ''))
      ),
    [tanks]
  );

  const selectedGuidedTank = useMemo(
    () => tanks.find((row) => String(row.id) === String(guidedContext.tank_id)),
    [tanks, guidedContext.tank_id]
  );

  const guidedTankNozzles = useMemo(() => {
    if (!guidedContext.tank_id) return [];
    return (meta.nozzles || []).filter(
      (row) => String(row.tank_id || '') === String(guidedContext.tank_id)
    );
  }, [meta.nozzles, guidedContext.tank_id]);

  const guidedSkippedDipDays = useMemo(() => {
    const previousDate = selectedGuidedTank?.last_dip_at ? String(selectedGuidedTank.last_dip_at).slice(0, 10) : '';
    const diff = dayDiff(previousDate, guidedContext.entry_date);
    if (!previousDate || diff == null || diff <= 1) return 0;
    return diff - 1;
  }, [selectedGuidedTank, guidedContext.entry_date]);

  const guidedTotalizerSummary = useMemo(() => {
    return guidedTotalizerRows.reduce(
      (summary, row) => {
        const opening = Number(row.opening_totalizer || 0);
        const closing = Number(row.closing_totalizer || 0);
        const testing = Number(row.testing_litres || 0);
        const rate = Number(row.rate || 0);
        const grossLitres = Math.max(closing - opening, 0);
        const netLitres = Math.max(grossLitres - Math.max(testing, 0), 0);
        return {
          grossLitres: summary.grossLitres + grossLitres,
          netLitres: summary.netLitres + netLitres,
          netAmount: summary.netAmount + netLitres * rate,
        };
      },
      { grossLitres: 0, netLitres: 0, netAmount: 0 }
    );
  }, [guidedTotalizerRows]);

  const isGuidedContextValid = Boolean(guidedContext.tank_id && guidedContext.entry_date);
  const isGuidedDipValid =
    Boolean(guidedContext.shift) && guidedDipForm.dip_cm !== '' && Number(guidedDipForm.dip_cm) > 0;
  const isGuidedReceiptValid =
    guidedReceiptChoice === 'no' ||
    (guidedReceiptChoice === 'yes' && Number(guidedReceiptForm.receipt_litres || 0) > 0);
  const areGuidedTotalizerRowsValid =
    guidedTankNozzles.length > 0 &&
    guidedTotalizerRows.length === guidedTankNozzles.length &&
    guidedTotalizerRows.every((row) => {
      const opening = Number(row.opening_totalizer || 0);
      const closing = Number(row.closing_totalizer || 0);
      const testing = Number(row.testing_litres || 0);
      return (
        row.opening_totalizer !== '' &&
        row.closing_totalizer !== '' &&
        closing > opening &&
        testing >= 0 &&
        testing <= closing - opening
      );
    });

  useEffect(() => {
    if (loading || guidedEntryDismissed || showGuidedEntry) return;
    if (!tanks.length || !(meta.nozzles || []).length) return;
    setShowGuidedEntry(true);
    if (!guidedContext.tank_id) {
      setGuidedContext((prev) => ({
        ...prev,
        tank_id: String(tanks[0]?.id || ''),
      }));
    }
  }, [loading, guidedEntryDismissed, showGuidedEntry, tanks, meta.nozzles, guidedContext.tank_id]);

  useEffect(() => {
    setGuidedReceiptForm((prev) => ({
      ...prev,
      time: defaultTimeForShift(guidedContext.shift) === '19:00' ? '18:00' : '09:00',
    }));
    setGuidedTotalizerTime(defaultTimeForShift(guidedContext.shift) === '19:00' ? '21:00' : '20:00');
  }, [guidedContext.shift]);

  useEffect(() => {
    setGuidedTotalizerRows(
      guidedTankNozzles.map((nozzle) => ({
        nozzle_id: String(nozzle.id),
        nozzle_name: nozzle.name || '',
        pump_name: nozzle.pump_name || '',
        opening_totalizer:
          nozzle.latest_closing_totalizer != null && nozzle.latest_closing_totalizer !== ''
            ? String(nozzle.latest_closing_totalizer)
            : '',
        opening_locked:
          nozzle.latest_closing_totalizer != null && nozzle.latest_closing_totalizer !== '',
        closing_totalizer: '',
        testing_litres: '',
        rate: nozzle.rate || '',
      }))
    );
  }, [guidedTankNozzles, guidedContext.entry_date]);

  const manualDerived = useMemo(() => {
    const opening = Number(manualTotalizerForm.opening_totalizer || 0);
    const closing = Number(manualTotalizerForm.closing_totalizer || 0);
    const testing = Number(manualTotalizerForm.testing_litres || 0);
    const rate = Number(manualTotalizerForm.rate || 0);
    const grossLitres = Math.max(closing - opening, 0);
    const netLitres = Math.max(grossLitres - Math.max(testing, 0), 0);
    return {
      grossLitres,
      netLitres,
      grossAmount: grossLitres * rate,
      netAmount: netLitres * rate,
    };
  }, [
    manualTotalizerForm.opening_totalizer,
    manualTotalizerForm.closing_totalizer,
    manualTotalizerForm.testing_litres,
    manualTotalizerForm.rate,
  ]);

  const tankVisual = useMemo(() => {
    if (!focusTank) return null;
    const capacity = Number(focusTank.tank_capacity || 0);
    const theoretical = Number(focusTank.current_theoretical_litres ?? focusTank.current_volume ?? 0);
    const physical = Number(focusTank.last_physical_litres ?? 0);
    const fillPercent = capacity > 0 ? clamp((theoretical / capacity) * 100, 0, 100) : 0;
    const physicalPercent = capacity > 0 ? clamp((physical / capacity) * 100, 0, 100) : 0;
    return {
      tone: getFuelTone(focusTank.fuel_type),
      capacity,
      theoretical,
      physical,
      fillPercent,
      physicalPercent,
      variance: Number(focusTank.variance_litres || 0),
    };
  }, [focusTank]);

  useEffect(() => {
    if (!manualTotalizerForm.tank_id) {
      setManualTotalizerForm((prev) => ({
        ...prev,
        nozzle_id: '',
        opening_totalizer: '',
        closing_totalizer: '',
        testing_litres: '',
        rate: '',
      }));
      return;
    }
    if (
      manualTotalizerForm.nozzle_id &&
      !manualTankNozzles.some((row) => String(row.id) === String(manualTotalizerForm.nozzle_id))
    ) {
      setManualTotalizerForm((prev) => ({
        ...prev,
        nozzle_id: '',
        opening_totalizer: '',
        closing_totalizer: '',
        testing_litres: '',
        rate: '',
      }));
    }
  }, [manualTotalizerForm.tank_id, manualTotalizerForm.nozzle_id, manualTankNozzles]);

  useEffect(() => {
    if (!selectedManualNozzle) {
      setManualTotalizerForm((prev) => ({
        ...prev,
        opening_totalizer: '',
        closing_totalizer: '',
        testing_litres: '',
        rate: '',
      }));
      return;
    }

    const mappedTank =
      tanks.find((row) => row.id && String(row.id) === String(selectedManualNozzle.tank_id || '')) || null;
    const latestClosing =
      selectedManualNozzle.latest_closing_totalizer != null && selectedManualNozzle.latest_closing_totalizer !== ''
        ? String(selectedManualNozzle.latest_closing_totalizer)
        : '';

    setManualTotalizerForm((prev) => ({
      ...prev,
      rate: selectedManualNozzle.rate || prev.rate || '',
      tank_id: mappedTank ? String(mappedTank.id) : '',
      opening_totalizer: latestClosing || prev.opening_totalizer || '',
    }));
  }, [selectedManualNozzle, tanks]);

  const manualOpeningLocked = useMemo(
    () =>
      selectedManualNozzle?.latest_closing_totalizer != null &&
      selectedManualNozzle?.latest_closing_totalizer !== '',
    [selectedManualNozzle]
  );

  const resetTankForm = () => {
    setEditingTankId(null);
    setTankForm({
      tank_name: '',
      fuel_type: availableFuelTypes[0] || 'Petrol',
      tank_capacity: '',
      current_volume: '',
      diameter_cm: '',
      length_cm: '',
      dead_stock_litres: '',
      is_active: true,
    });
  };

  const saveTank = async () => {
    try {
      setSaving(true);
      const payload = { ...tankForm };
      if (editingTankId) {
        await api.put(`/api/fuel/${editingTankId}`, payload, { headers });
      } else {
        await api.post('/api/fuel', payload, { headers });
      }
      resetTankForm();
      await fetchMeta();
      await fetchTanks();
    } finally {
      setSaving(false);
    }
  };

  const editTank = (tank) => {
    setEditingTankId(tank.id);
    setTankForm({
      tank_name: tank.tank_name || '',
      fuel_type: tank.fuel_type || 'Petrol',
      tank_capacity: tank.tank_capacity || '',
      current_volume: tank.current_volume || '',
      diameter_cm: tank.diameter_cm || '',
      length_cm: tank.length_cm || '',
      dead_stock_litres: tank.dead_stock_litres || '',
      is_active: tank.is_active === 1 || tank.is_active === true,
    });
  };

  const saveDip = async () => {
    try {
      setSaving(true);
      await api.post('/api/fuel/dips', dipForm, { headers });
      setDipForm((prev) => ({
        ...prev,
        dip_cm: '',
        water_cm: '',
        remarks: '',
        recorded_at: todayDateTime(),
      }));
      await fetchTanks();
      await fetchDipEntries();
      await fetchDsr();
    } finally {
      setSaving(false);
    }
  };

  const saveDelivery = async () => {
    try {
      setSaving(true);
      await api.post('/api/fuel/deliveries', deliveryForm, { headers });
      setDeliveryForm((prev) => ({
        ...prev,
        invoice_no: '',
        supplier_name: '',
        receipt_litres: '',
        remarks: '',
        delivery_date: todayDateTime(),
      }));
      await fetchTanks();
      await fetchDeliveries();
      await fetchDsr();
    } finally {
      setSaving(false);
    }
  };

  const saveManualTotalizer = async () => {
    try {
      setSaving(true);
      await api.post('/api/fuel/manual-totalizer', manualTotalizerForm, { headers });
      setManualTotalizerForm((prev) => ({
        ...prev,
        nozzle_id: '',
        opening_totalizer: '',
        closing_totalizer: '',
        testing_litres: '',
        rate: '',
        remarks: '',
        entry_datetime: todayDateTime(),
      }));
      await fetchMeta();
      await fetchTanks();
      await fetchManualTotalizerEntries();
      await fetchTotalizer();
      await fetchDsr();
    } finally {
      setSaving(false);
    }
  };

  const resetGuidedEntry = () => {
    setGuidedStep(0);
    setGuidedSaving(false);
    setGuidedContext({
      tank_id: String(tanks[0]?.id || ''),
      entry_date: todayDate(),
      shift: 'morning',
    });
    setGuidedDipForm({
      dip_cm: '',
      water_cm: '',
      remarks: '',
    });
    setGuidedReceiptChoice('');
    setGuidedReceiptForm({
      time: '09:00',
      invoice_no: '',
      supplier_name: '',
      receipt_litres: '',
      remarks: '',
    });
    setGuidedTotalizerTime('21:00');
    setGuidedTotalizerRows([]);
  };

  const closeGuidedEntry = () => {
    setShowGuidedEntry(false);
    setGuidedEntryDismissed(true);
    resetGuidedEntry();
  };

  const openGuidedEntry = () => {
    setGuidedEntryDismissed(false);
    resetGuidedEntry();
    setShowGuidedEntry(true);
  };

  const handleGuidedNext = () => {
    if (guidedStep === 0 && isGuidedContextValid) {
      setGuidedStep(1);
      return;
    }
    if (guidedStep === 1 && isGuidedDipValid) {
      setGuidedStep(2);
      return;
    }
    if (guidedStep === 2 && isGuidedReceiptValid) {
      setGuidedStep(3);
    }
  };

  const updateGuidedTotalizerRow = (nozzleId, patch) => {
    setGuidedTotalizerRows((prev) =>
      prev.map((row) =>
        String(row.nozzle_id) === String(nozzleId)
          ? { ...row, ...patch }
          : row
      )
    );
  };

  const saveGuidedEntries = async () => {
    if (!isGuidedContextValid || !isGuidedDipValid || !isGuidedReceiptValid || !areGuidedTotalizerRowsValid) return;

    try {
      setGuidedSaving(true);

      await api.post(
        '/api/fuel/dips',
        {
          tank_id: guidedContext.tank_id,
          recorded_at: combineDateAndTime(guidedContext.entry_date, defaultTimeForShift(guidedContext.shift)),
          shift: startCaseShift(guidedContext.shift),
          entry_type: 'manual',
          dip_cm: guidedDipForm.dip_cm,
          water_cm: guidedDipForm.water_cm || null,
          remarks: guidedDipForm.remarks || null,
        },
        { headers }
      );

      if (guidedReceiptChoice === 'yes') {
        await api.post(
          '/api/fuel/deliveries',
          {
            tank_id: guidedContext.tank_id,
            delivery_date: combineDateAndTime(guidedContext.entry_date, guidedReceiptForm.time),
            invoice_no: guidedReceiptForm.invoice_no || null,
            supplier_name: guidedReceiptForm.supplier_name || null,
            receipt_litres: guidedReceiptForm.receipt_litres,
            remarks: guidedReceiptForm.remarks || null,
          },
          { headers }
        );
      }

      for (const row of guidedTotalizerRows) {
        await api.post(
          '/api/fuel/manual-totalizer',
          {
            tank_id: guidedContext.tank_id,
            nozzle_id: row.nozzle_id,
            entry_datetime: combineDateAndTime(guidedContext.entry_date, guidedTotalizerTime),
            opening_totalizer: row.opening_totalizer,
            closing_totalizer: row.closing_totalizer,
            testing_litres: row.testing_litres || 0,
            rate: row.rate || 0,
            remarks: null,
          },
          { headers }
        );
      }

      await fetchAll();
      setShowGuidedEntry(false);
      setGuidedEntryDismissed(true);
      resetGuidedEntry();
      window.alert('Entries saved. Check your DSR books now.');
    } catch (error) {
      window.alert(error?.response?.data?.error || error?.message || 'Unable to save guided stock entries right now.');
    } finally {
      setGuidedSaving(false);
    }
  };

  const updateNozzleTankMapping = async (nozzleId, tankId) => {
    try {
      setMappingBusyId(String(nozzleId));
      await api.patch(`/api/nozzles/${nozzleId}/tank-mapping`, { tank_id: tankId || null }, { headers });
      await fetchMeta();
    } finally {
      setMappingBusyId(null);
    }
  };

  const handleMappingSelectionChange = async (nozzleId, nextTankId) => {
    await updateNozzleTankMapping(nozzleId, nextTankId || null);
  };

  const handleMappingDragStart = (nozzle, event) => {
    setDraggingNozzleId(String(nozzle.id));
    if (event?.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      try {
        event.dataTransfer.setData('text/plain', String(nozzle.id));
      } catch (err) {
        // ignore browser drag data limitations
      }
    }
  };

  const handleMappingDragEnd = () => {
    setDraggingNozzleId('');
    setDragPointer(null);
  };

  const handleBoardDragOver = (event) => {
    if (!draggingNozzleId || !mappingBoardRef.current) return;
    event.preventDefault();
    const boardRect = mappingBoardRef.current.getBoundingClientRect();
    setDragPointer({
      x: event.clientX - boardRect.left,
      y: event.clientY - boardRect.top,
    });

    const maybeAutoScroll = (container) => {
      if (!container) return;
      const rect = container.getBoundingClientRect();
      if (
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom
      ) {
        return;
      }

      const threshold = 84;
      const topDistance = event.clientY - rect.top;
      const bottomDistance = rect.bottom - event.clientY;

      if (topDistance < threshold) {
        const delta = Math.max(8, ((threshold - topDistance) / threshold) * 22);
        container.scrollBy({ top: -delta, behavior: 'auto' });
      } else if (bottomDistance < threshold) {
        const delta = Math.max(8, ((threshold - bottomDistance) / threshold) * 22);
        container.scrollBy({ top: delta, behavior: 'auto' });
      }
    };

    maybeAutoScroll(tankListRef.current);
    maybeAutoScroll(nozzleListRef.current);
  };

  const handleTankDrop = async (tank, event) => {
    event.preventDefault();
    const dragId = draggingNozzleId || event?.dataTransfer?.getData('text/plain');
    const nozzle = mappingNozzles.find((row) => String(row.id) === String(dragId));
    if (!nozzle) {
      handleMappingDragEnd();
      return;
    }
    if (normalizeFuelKey(nozzle.fuel_type) !== normalizeFuelKey(tank.fuel_type)) {
      handleMappingDragEnd();
      return;
    }
    await updateNozzleTankMapping(nozzle.id, tank.id);
    handleMappingDragEnd();
  };

  const handleUnmapDrop = async (event) => {
    event.preventDefault();
    const dragId = draggingNozzleId || event?.dataTransfer?.getData('text/plain');
    const nozzle = mappingNozzles.find((row) => String(row.id) === String(dragId));
    if (!nozzle) {
      handleMappingDragEnd();
      return;
    }
    await updateNozzleTankMapping(nozzle.id, null);
    handleMappingDragEnd();
  };

  return (
    <div className="stock-page">
      {showGuidedEntry && (
        <div className="stock-guided-overlay" onClick={closeGuidedEntry}>
          <div className="stock-guided-modal" onClick={(event) => event.stopPropagation()}>
            <div className="stock-guided-head">
              <div>
                <span>Guided Fuel Entry</span>
                <h3>Capture stock, receipt, and totalizer in one flow</h3>
              </div>
              <button type="button" className="stock-guided-close" onClick={closeGuidedEntry}>
                Close
              </button>
            </div>

            <div className="stock-guided-steps">
              {['Tank & Date', 'Dip Entry', 'Receipt', 'Totalizer'].map((label, index) => (
                <div
                  key={label}
                  className={`stock-guided-step-pill ${
                    guidedStep === index ? 'active' : guidedStep > index ? 'done' : ''
                  }`}
                >
                  <strong>{index + 1}</strong>
                  <span>{label}</span>
                </div>
              ))}
            </div>

            <div className="stock-guided-body">
              {guidedStep === 0 && (
                <div className="stock-guided-panel">
                  <div className="stock-form-grid">
                    <label>
                      <span>Select tank</span>
                      <select
                        value={guidedContext.tank_id}
                        onChange={(e) => setGuidedContext((prev) => ({ ...prev, tank_id: e.target.value }))}
                      >
                        <option value="">Select tank</option>
                        {manualAvailableTanks.map((tank) => (
                          <option key={tank.id} value={tank.id}>
                            {tank.tank_name || tank.fuel_type} - {tank.fuel_type}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label>
                      <span>Entry date</span>
                      <input
                        type="date"
                        value={guidedContext.entry_date}
                        onChange={(e) => setGuidedContext((prev) => ({ ...prev, entry_date: e.target.value }))}
                      />
                    </label>
                    <div className="stock-filter-note">
                      <span>Carry forward context</span>
                      <strong>
                        {selectedGuidedTank?.tank_name || selectedGuidedTank?.fuel_type || 'Choose tank first'}
                      </strong>
                    </div>
                  </div>

                  {selectedGuidedTank?.last_dip_at && (
                    <div className={`stock-inline-empty ${guidedSkippedDipDays > 0 ? '' : 'stock-inline-info'}`}>
                      Last dip for this tank was on {String(selectedGuidedTank.last_dip_at).slice(0, 10)}.
                      {guidedSkippedDipDays > 0
                        ? ` You are skipping ${guidedSkippedDipDays} day${guidedSkippedDipDays > 1 ? 's' : ''} before this entry.`
                        : ' This date can continue from the previous stock day.'}
                    </div>
                  )}
                </div>
              )}

              {guidedStep === 1 && (
                <div className="stock-guided-panel">
                  <div className="stock-guided-context">
                    <span>{selectedGuidedTank?.tank_name || selectedGuidedTank?.fuel_type}</span>
                    <strong>{guidedContext.entry_date}</strong>
                  </div>
                  <div className="stock-form-grid">
                    <label>
                      <span>Shift</span>
                      <select
                        value={guidedContext.shift}
                        onChange={(e) => setGuidedContext((prev) => ({ ...prev, shift: e.target.value }))}
                      >
                        <option value="morning">Morning</option>
                        <option value="night">Night</option>
                      </select>
                    </label>
                    <label>
                      <span>Dip (cm)</span>
                      <input
                        type="number"
                        step="0.001"
                        value={guidedDipForm.dip_cm}
                        onChange={(e) => setGuidedDipForm((prev) => ({ ...prev, dip_cm: e.target.value }))}
                      />
                    </label>
                    <label>
                      <span>Water (cm)</span>
                      <input
                        type="number"
                        step="0.001"
                        value={guidedDipForm.water_cm}
                        onChange={(e) => setGuidedDipForm((prev) => ({ ...prev, water_cm: e.target.value }))}
                      />
                    </label>
                  </div>
                  <label className="stock-full-width">
                    <span>Remarks</span>
                    <textarea
                      rows="3"
                      value={guidedDipForm.remarks}
                      onChange={(e) => setGuidedDipForm((prev) => ({ ...prev, remarks: e.target.value }))}
                    />
                  </label>
                </div>
              )}

              {guidedStep === 2 && (
                <div className="stock-guided-panel">
                  <div className="stock-guided-context">
                    <span>{selectedGuidedTank?.tank_name || selectedGuidedTank?.fuel_type}</span>
                    <strong>{guidedContext.entry_date}</strong>
                  </div>
                  <div className="stock-guided-choice-row">
                    <button
                      type="button"
                      className={`stock-guided-choice ${guidedReceiptChoice === 'yes' ? 'active' : ''}`}
                      onClick={() => setGuidedReceiptChoice('yes')}
                    >
                      Yes, receipt available
                    </button>
                    <button
                      type="button"
                      className={`stock-guided-choice ${guidedReceiptChoice === 'no' ? 'active' : ''}`}
                      onClick={() => setGuidedReceiptChoice('no')}
                    >
                      No receipt today
                    </button>
                  </div>

                  {guidedReceiptChoice === 'yes' && (
                    <div className="stock-form-grid">
                      <label>
                        <span>Receipt time</span>
                        <input
                          type="time"
                          value={guidedReceiptForm.time}
                          onChange={(e) => setGuidedReceiptForm((prev) => ({ ...prev, time: e.target.value }))}
                        />
                      </label>
                      <label>
                        <span>Receipt litres</span>
                        <input
                          type="number"
                          step="0.001"
                          value={guidedReceiptForm.receipt_litres}
                          onChange={(e) => setGuidedReceiptForm((prev) => ({ ...prev, receipt_litres: e.target.value }))}
                        />
                      </label>
                      <label>
                        <span>Invoice no</span>
                        <input
                          value={guidedReceiptForm.invoice_no}
                          onChange={(e) => setGuidedReceiptForm((prev) => ({ ...prev, invoice_no: e.target.value }))}
                        />
                      </label>
                      <label>
                        <span>Supplier name</span>
                        <input
                          value={guidedReceiptForm.supplier_name}
                          onChange={(e) => setGuidedReceiptForm((prev) => ({ ...prev, supplier_name: e.target.value }))}
                        />
                      </label>
                      <label className="stock-full-width">
                        <span>Receipt remarks</span>
                        <textarea
                          rows="3"
                          value={guidedReceiptForm.remarks}
                          onChange={(e) => setGuidedReceiptForm((prev) => ({ ...prev, remarks: e.target.value }))}
                        />
                      </label>
                    </div>
                  )}

                  {guidedReceiptChoice === 'no' && (
                    <div className="stock-inline-empty stock-inline-info">
                      No problem. We will skip receipt and move to totalizer entry for the same tank and date.
                    </div>
                  )}
                </div>
              )}

              {guidedStep === 3 && (
                <div className="stock-guided-panel">
                  <div className="stock-guided-context">
                    <span>{selectedGuidedTank?.tank_name || selectedGuidedTank?.fuel_type}</span>
                    <strong>{guidedContext.entry_date}</strong>
                  </div>
                  <div className="stock-form-grid">
                    <div className="stock-filter-note">
                      <span>Selected tank</span>
                      <strong>{selectedGuidedTank?.tank_name || selectedGuidedTank?.fuel_type || '-'}</strong>
                    </div>
                    <div className="stock-filter-note">
                      <span>Totalizer date</span>
                      <strong>{guidedContext.entry_date || '-'}</strong>
                    </div>
                    <label>
                      <span>Entry time</span>
                      <input
                        type="time"
                        value={guidedTotalizerTime}
                        onChange={(e) => setGuidedTotalizerTime(e.target.value)}
                      />
                    </label>
                  </div>

                  {!guidedTankNozzles.length && (
                    <div className="stock-inline-empty">
                      No mapped nozzles found for this tank. Close this popup and use the main stock screen to complete mapping first.
                    </div>
                  )}

                  {!!guidedTankNozzles.length && (
                    <>
                      <div className="stock-guided-totalizer-list">
                        {guidedTotalizerRows.map((row) => (
                          <div key={row.nozzle_id} className="stock-guided-totalizer-card">
                            <div className="stock-guided-totalizer-head">
                              <div>
                                <strong>{row.nozzle_name}</strong>
                                <span>{row.pump_name || 'Pump not linked'}</span>
                              </div>
                            </div>
                            <div className="stock-guided-totalizer-grid">
                              <label>
                                <span>Opening</span>
                                <input
                                  type="number"
                                  step="0.001"
                                  value={row.opening_totalizer}
                                  readOnly={row.opening_locked}
                                  onChange={(e) => updateGuidedTotalizerRow(row.nozzle_id, { opening_totalizer: e.target.value })}
                                />
                              </label>
                              <label>
                                <span>Closing</span>
                                <input
                                  type="number"
                                  step="0.001"
                                  value={row.closing_totalizer}
                                  onChange={(e) => updateGuidedTotalizerRow(row.nozzle_id, { closing_totalizer: e.target.value })}
                                />
                              </label>
                              <label>
                                <span>Testing litres</span>
                                <input
                                  type="number"
                                  step="0.001"
                                  value={row.testing_litres}
                                  onChange={(e) => updateGuidedTotalizerRow(row.nozzle_id, { testing_litres: e.target.value })}
                                />
                              </label>
                              <label>
                                <span>Rate</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={row.rate}
                                  onChange={(e) => updateGuidedTotalizerRow(row.nozzle_id, { rate: e.target.value })}
                                />
                              </label>
                            </div>
                            {row.closing_totalizer !== '' && Number(row.closing_totalizer) <= Number(row.opening_totalizer || 0) && (
                              <div className="stock-inline-empty">
                                Closing totalizer should be greater than opening for {row.nozzle_name}.
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      <div className="stock-inline-metric">
                        <div>
                          <span>Gross sales</span>
                          <strong>{qty(guidedTotalizerSummary.grossLitres)} L</strong>
                        </div>
                        <div>
                          <span>Net sales</span>
                          <strong>{qty(guidedTotalizerSummary.netLitres)} L</strong>
                        </div>
                        <div>
                          <span>Net amount</span>
                          <strong>{money(guidedTotalizerSummary.netAmount)}</strong>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="stock-guided-footer">
              <button
                type="button"
                className="stock-btn ghost"
                onClick={() => setGuidedStep((prev) => Math.max(prev - 1, 0))}
                disabled={guidedStep === 0 || guidedSaving}
              >
                Back
              </button>

              {guidedStep < 3 ? (
                <button
                  type="button"
                  className="stock-btn primary"
                  onClick={handleGuidedNext}
                  disabled={
                    guidedSaving ||
                    (guidedStep === 0 && !isGuidedContextValid) ||
                    (guidedStep === 1 && !isGuidedDipValid) ||
                    (guidedStep === 2 && !isGuidedReceiptValid)
                  }
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  className="stock-btn primary"
                  onClick={saveGuidedEntries}
                  disabled={guidedSaving || !areGuidedTotalizerRowsValid}
                >
                  {guidedSaving ? 'Saving...' : 'Save Entries'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <section className="stock-hero">
        <div className="stock-hero-copy">
          <div className="stock-kicker">Fuel Intelligence</div>
          <h2>Totalizer, DSR, and dip entry</h2>
          <p>
            Track tank-level physical stock, manual nozzle-wise totalizer movement, stock receipts,
            and DSR variance from one control room without depending on reconciliation.
          </p>
        </div>
        <div className="stock-hero-actions">
          <button type="button" className="stock-btn ghost" onClick={openGuidedEntry}>
            Guided Entry
          </button>
          <button type="button" className="stock-btn ghost" onClick={fetchAll}>
            Refresh
          </button>
        </div>
      </section>

      <section className="stock-visual-band">
        <div className="stock-card stock-tank-visual-card">
          <div className="stock-card-head">
            <div>
              <span>Live Tank View</span>
              <h3>{focusTank ? (focusTank.tank_name || focusTank.fuel_type || `Tank ${focusTank.id}`) : 'No tank yet'}</h3>
            </div>
            {focusTank && (
              <div className={`stock-fuel-badge ${tankVisual?.tone || 'petrol'}`}>
                {focusTank.fuel_type || 'Fuel'}
              </div>
            )}
          </div>

          {focusTank && tankVisual ? (
            <div className="stock-tank-visual-layout">
              <div className={`stock-tank-shell ${tankVisual.tone}`}>
                <div className="stock-tank-cap" />
                <div className="stock-tank-window">
                  <div
                    className="stock-tank-liquid"
                    style={{ height: `${tankVisual.fillPercent}%` }}
                  >
                    <div className="stock-tank-wave stock-tank-wave-a" />
                    <div className="stock-tank-wave stock-tank-wave-b" />
                  </div>
                  <div
                    className="stock-tank-physical-line"
                    style={{ bottom: `${tankVisual.physicalPercent}%` }}
                  />
                </div>
                <div className="stock-tank-percent">{tankVisual.fillPercent.toFixed(0)}%</div>
              </div>

              <div className="stock-tank-metrics">
                <div className="stock-tank-metric">
                  <span>Theoretical stock</span>
                  <strong>{qty(tankVisual.theoretical)} L</strong>
                </div>
                <div className="stock-tank-metric">
                  <span>Tank capacity</span>
                  <strong>{qty(tankVisual.capacity)} L</strong>
                </div>
                <div className="stock-tank-metric">
                  <span>Last physical</span>
                  <strong>{focusTank.last_physical_litres == null ? '-' : `${qty(tankVisual.physical)} L`}</strong>
                </div>
                <div className="stock-tank-metric">
                  <span>Variance</span>
                  <strong>{focusTank.variance_litres == null ? '-' : `${qty(tankVisual.variance)} L`}</strong>
                </div>
                <div className="stock-tank-meta-row">
                  <div>
                    <span>Last dip</span>
                    <strong>{focusTank.last_dip_at ? toDateTimeLocal(focusTank.last_dip_at).replace('T', ' ') : 'Not recorded'}</strong>
                  </div>
                  <div>
                    <span>Mapped nozzles</span>
                    <strong>{Number(focusTank.nozzle_count || 0)}</strong>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="stock-inline-empty">Create a tank to start live stock tracking and animated level view.</div>
          )}
        </div>

        <div className="stock-card stock-tank-list-card">
          <div className="stock-card-head">
            <div>
              <span>Tank Focus</span>
              <h3>Switch between tanks quickly</h3>
            </div>
          </div>

          <div className="stock-tank-chip-list">
            {tanks.map((tank) => {
              const isActive = String((focusTank || {}).id || '') === String(tank.id);
              const level = Number(tank.tank_capacity || 0) > 0
                ? clamp((Number(tank.current_theoretical_litres ?? tank.current_volume ?? 0) / Number(tank.tank_capacity)) * 100, 0, 100)
                : 0;
              return (
                <button
                  key={tank.id}
                  type="button"
                  className={`stock-tank-chip ${isActive ? 'active' : ''}`}
                  onClick={() => {
                    setFilters((prev) => ({ ...prev, tank_id: String(tank.id) }));
                    setDipForm((prev) => ({ ...prev, tank_id: String(tank.id) }));
                    setDeliveryForm((prev) => ({ ...prev, tank_id: String(tank.id) }));
                  }}
                >
                  <div className="stock-tank-chip-top">
                    <strong>{tank.tank_name || tank.fuel_type || `Tank ${tank.id}`}</strong>
                    <span>{tank.fuel_type || 'Fuel'}</span>
                  </div>
                  <div className="stock-tank-chip-bar">
                    <div
                      className={`stock-tank-chip-fill ${getFuelTone(tank.fuel_type)}`}
                      style={{ width: `${level}%` }}
                    />
                  </div>
                  <div className="stock-tank-chip-bottom">
                    <span>{qty(tank.current_theoretical_litres ?? tank.current_volume ?? 0)} L</span>
                    <span>{level.toFixed(0)}%</span>
                  </div>
                </button>
              );
            })}
            {!tanks.length && (
              <div className="stock-inline-empty">No tanks created yet.</div>
            )}
          </div>
        </div>
      </section>

      <section className="stock-summary-grid">
        <article className="stock-summary-card blue">
          <span>Theoretical stock</span>
          <strong>{qty(summary.theoretical)} L</strong>
          <em>Current physical litres across all tanks</em>
        </article>
        <article className="stock-summary-card green">
          <span>Last physical stock</span>
          <strong>{qty(summary.physical)} L</strong>
          <em>Based on latest dip entries</em>
        </article>
        <article className="stock-summary-card amber">
          <span>Variance</span>
          <strong>{qty(summary.variance)} L</strong>
          <em>Theoretical minus latest physical</em>
        </article>
        <article className="stock-summary-card rose">
          <span>Missing today dip</span>
          <strong>{summary.missingDip}</strong>
          <em>Tanks without a dip recorded today</em>
        </article>
      </section>

      {(setupAlerts.missingGeometry.length > 0 || !tanks.length) && (
        <section className="stock-card">
          <div className="stock-card-head">
            <div>
              <span>Setup Checklist</span>
              <h3>Finish tank setup before daily dip capture</h3>
            </div>
          </div>
          <div className="stock-checklist">
            <div>
              1. Create or update each tank with its tank name, fuel type, diameter, length, and dead stock.
            </div>
            <div>
              2. Map each nozzle to the correct tank from Pump &amp; Nozzles so totalizer and DSR split cleanly.
            </div>
            {setupAlerts.missingGeometry.length > 0 && (
              <div className="stock-checklist-alert">
                Geometry missing for: {setupAlerts.missingGeometry.join(', ')}
              </div>
            )}
          </div>
        </section>
      )}

      <section className="stock-grid two-up">
        <div className="stock-card">
          <div className="stock-card-head">
            <div>
              <span>Tank Master</span>
              <h3>{editingTankId ? 'Edit tank' : 'Create tank'}</h3>
            </div>
            <button type="button" className="stock-btn ghost" onClick={resetTankForm}>
              Clear
            </button>
          </div>

          <div className="stock-form-grid">
            <label>
              <span>Tank name</span>
              <input value={tankForm.tank_name} onChange={(e) => setTankForm({ ...tankForm, tank_name: e.target.value })} />
            </label>
            <label>
              <span>Fuel type</span>
              <select value={tankForm.fuel_type} onChange={(e) => setTankForm({ ...tankForm, fuel_type: e.target.value })}>
                {availableFuelTypes.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Capacity (L)</span>
              <input type="number" value={tankForm.tank_capacity} onChange={(e) => setTankForm({ ...tankForm, tank_capacity: e.target.value })} />
            </label>
            <label>
              <span>Opening stock fallback (L)</span>
              <input type="number" value={tankForm.current_volume} onChange={(e) => setTankForm({ ...tankForm, current_volume: e.target.value })} />
            </label>
            <label>
              <span>Diameter (cm)</span>
              <input type="number" step="0.001" value={tankForm.diameter_cm} onChange={(e) => setTankForm({ ...tankForm, diameter_cm: e.target.value })} />
            </label>
            <label>
              <span>Length (cm)</span>
              <input type="number" step="0.001" value={tankForm.length_cm} onChange={(e) => setTankForm({ ...tankForm, length_cm: e.target.value })} />
            </label>
            <label>
              <span>Dead stock (L)</span>
              <input type="number" value={tankForm.dead_stock_litres} onChange={(e) => setTankForm({ ...tankForm, dead_stock_litres: e.target.value })} />
            </label>
            <label className="stock-toggle">
              <span>Active</span>
              <input type="checkbox" checked={tankForm.is_active} onChange={(e) => setTankForm({ ...tankForm, is_active: e.target.checked })} />
            </label>
          </div>

          <div className="stock-actions-row">
            <button type="button" className="stock-btn primary" onClick={saveTank} disabled={saving}>
              {saving ? 'Saving...' : editingTankId ? 'Update Tank' : 'Add Tank'}
            </button>
          </div>
        </div>

        <div className="stock-card">
          <div className="stock-card-head">
            <div>
              <span>Dip Entry</span>
              <h3>Capture physical stock</h3>
            </div>
          </div>

          <div className="stock-form-grid">
            <label>
              <span>Tank</span>
              <select value={dipForm.tank_id} onChange={(e) => setDipForm({ ...dipForm, tank_id: e.target.value })}>
                <option value="">Select tank</option>
                {tanks.map((tank) => (
                  <option key={tank.id} value={tank.id}>{tank.tank_name || tank.fuel_type}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Recorded at</span>
              <input type="datetime-local" value={dipForm.recorded_at} onChange={(e) => setDipForm({ ...dipForm, recorded_at: e.target.value })} />
            </label>
            <label>
              <span>Shift</span>
              <input value={dipForm.shift} onChange={(e) => setDipForm({ ...dipForm, shift: e.target.value })} placeholder="Morning / Night" />
            </label>
            <label>
              <span>Entry type</span>
              <select value={dipForm.entry_type} onChange={(e) => setDipForm({ ...dipForm, entry_type: e.target.value })}>
                <option value="opening">Opening</option>
                <option value="closing">Closing</option>
                <option value="manual">Manual</option>
              </select>
            </label>
            <label>
              <span>Dip (cm)</span>
              <input type="number" step="0.001" value={dipForm.dip_cm} onChange={(e) => setDipForm({ ...dipForm, dip_cm: e.target.value })} />
            </label>
            <label>
              <span>Water (cm)</span>
              <input type="number" step="0.001" value={dipForm.water_cm} onChange={(e) => setDipForm({ ...dipForm, water_cm: e.target.value })} />
            </label>
          </div>

          {dipPreview && (
            <div className="stock-inline-metric">
              <div>
                <span>Physical volume</span>
                <strong>{wholeQty(dipPreview.physical_volume_litres)} L</strong>
              </div>
              <div>
                <span>Diff / mm</span>
                <strong>{qty(dipPreview.diff_litres_per_mm)} L</strong>
              </div>
            </div>
          )}

          {dipForm.tank_id && !dipPreview && (
            <div className="stock-inline-empty">
              Add tank diameter and length first to generate the dip chart and enable dip entry.
            </div>
          )}

          <label className="stock-full-width">
            <span>Remarks</span>
            <textarea rows="3" value={dipForm.remarks} onChange={(e) => setDipForm({ ...dipForm, remarks: e.target.value })} />
          </label>

          <div className="stock-actions-row">
            <button type="button" className="stock-btn primary" onClick={saveDip} disabled={saving}>
              {saving ? 'Saving...' : 'Save Dip'}
            </button>
          </div>
        </div>
      </section>

      <section className="stock-card">
        <div className="stock-card-head">
          <div>
            <span>Nozzle Mapping</span>
            <h3>Assign each nozzle to the right tank by fuel type</h3>
          </div>
          {showMappingBoard ? (
            <div className="stock-head-actions">
              <button
                type="button"
                className="stock-btn ghost"
                onClick={() => setShowMappingBoard(false)}
              >
                Hide Mapping
              </button>
            </div>
          ) : null}
        </div>

        {!mappingNozzles.length || !tanks.length ? (
          <div className="stock-inline-empty">
            Create tanks and nozzles first to start mapping them together.
          </div>
        ) : !showMappingBoard ? (
          <div className="stock-mapping-summary-card">
            <div className="stock-mapping-summary-copy">
              <strong>Mapping ready</strong>
              <span>
                {mappingSummary.mappedCount} / {mappingSummary.totalNozzles} nozzles mapped across{' '}
                {mappingSummary.coveredTanks} / {mappingSummary.totalTanks} tanks.
              </span>
            </div>
            <button
              type="button"
              className="stock-btn ghost"
              onClick={() => setShowMappingBoard(true)}
            >
              Show Mapping
            </button>
          </div>
        ) : (
          <div className="stock-mapping-workbench-wrap">
            <div className="stock-mapping-stats">
              <div className="stock-mapping-stat">
                <span>Nozzles mapped</span>
                <strong>{mappingSummary.mappedCount} / {mappingSummary.totalNozzles}</strong>
              </div>
              <div className="stock-mapping-stat">
                <span>Tanks covered</span>
                <strong>{mappingSummary.coveredTanks} / {mappingSummary.totalTanks}</strong>
              </div>
              <div className="stock-mapping-stat stock-mapping-stat-accent">
                <span>Best flow</span>
                <strong>Select tank directly from each nozzle card</strong>
              </div>
            </div>

            <div className="stock-mapping-workbench stock-mapping-workbench-simple">
              <div className="stock-mapping-column">
                <div className="stock-mapping-column-head">
                  <strong>Nozzle assignments</strong>
                  <span>Pick a compatible tank for each nozzle and save instantly</span>
                </div>
                <div className="stock-mapping-node-list stock-mapping-assignment-list">
                  {mappingNozzles.map((nozzle) => {
                    const mappedTank = tanks.find((tank) => String(tank.id) === String(nozzle.tank_id || ''));
                    const compatibleTanks = tanks.filter(
                      (tank) => normalizeFuelKey(tank.fuel_type) === normalizeFuelKey(nozzle.fuel_type)
                    );
                    return (
                      <article
                        key={nozzle.id}
                        className={`stock-mapping-node stock-mapping-node-nozzle stock-mapping-assignment-card ${
                          nozzle.tank_id ? 'mapped' : 'unmapped'
                        }`}
                      >
                        <div className="stock-mapping-node-main">
                          <div className="stock-mapping-meta-grid">
                            <div className="stock-mapping-meta-item">
                              <label>Nozzle</label>
                              <strong>{nozzle.name || '-'}</strong>
                            </div>
                            <div className="stock-mapping-meta-item">
                              <label>Pump</label>
                              <strong>{nozzle.pump_name || '-'}</strong>
                            </div>
                            <div className="stock-mapping-meta-item">
                              <label>Fuel Type</label>
                              <strong>{nozzle.fuel_type || '-'}</strong>
                            </div>
                          </div>
                        </div>
                        <div className="stock-mapping-node-status">
                          <label>Mapped Tank</label>
                          <em>{mappedTank ? mappedTank.tank_name || mappedTank.fuel_type : 'Unmapped'}</em>
                        </div>

                        <div className="stock-mapping-assignment-controls">
                          <label className="stock-mapping-assignment-field">
                            <span>Choose Tank</span>
                            <select
                              value={nozzle.tank_id || ''}
                              disabled={mappingBusyId === String(nozzle.id)}
                              onChange={(event) => handleMappingSelectionChange(nozzle.id, event.target.value)}
                            >
                              <option value="">Select compatible tank</option>
                              {compatibleTanks.map((tank) => (
                                <option key={tank.id} value={tank.id}>
                                  {tank.tank_name || `Tank ${tank.id}`} ({tank.fuel_type || 'Fuel'})
                                </option>
                              ))}
                            </select>
                          </label>

                          <div className="stock-mapping-assignment-actions">
                            <span className="stock-mapping-inline-note">
                              {compatibleTanks.length} compatible tank{compatibleTanks.length === 1 ? '' : 's'}
                            </span>
                            <button
                              type="button"
                              className="stock-btn ghost stock-mapping-inline-btn"
                              disabled={!nozzle.tank_id || mappingBusyId === String(nozzle.id)}
                              onClick={() => handleMappingSelectionChange(nozzle.id, '')}
                            >
                              Unmap
                            </button>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>

              <div className="stock-mapping-column">
                <div className="stock-mapping-column-head">
                  <strong>Tank summary</strong>
                  <span>See current nozzle coverage tank by tank</span>
                </div>
                <div className="stock-mapping-target-list stock-mapping-summary-list">
                  {nozzleMappingBoard.map((tank) => {
                    return (
                      <article
                        key={tank.id}
                        className="stock-mapping-target stock-mapping-target-static"
                      >
                        <div className="stock-mapping-target-head">
                          <div className="stock-mapping-target-title">
                            <label>Tank</label>
                            <strong>{tank.tank_name || `Tank ${tank.id}`}</strong>
                            <span>{tank.fuel_type || 'Fuel'} tank</span>
                          </div>
                          <em>{tank.mappedNozzles.length} mapped</em>
                        </div>

                        <div className="stock-mapping-target-visual">
                          <div className={`stock-mapping-tank-shell fuel-${normalizeFuelKey(tank.fuel_type) || 'default'}`}>
                            {tank.mappedNozzles.length ? (
                              <div className="stock-mapping-nozzle-lane">
                                {tank.mappedNozzles.map((nozzle) => (
                                  <div
                                    key={nozzle.id}
                                    className="stock-mapping-nozzle-badge"
                                    title={`${nozzle.name} mapped to ${tank.tank_name || `Tank ${tank.id}`}`}
                                  >
                                    <span className="stock-mapping-nozzle-badge-icon" />
                                    <span className="stock-mapping-nozzle-badge-text">{nozzle.name}</span>
                                  </div>
                                ))}
                              </div>
                            ) : null}
                            <div className="stock-mapping-tank-cap stock-mapping-tank-cap-left" />
                            <div className="stock-mapping-tank-cap stock-mapping-tank-cap-right" />
                            <div className="stock-mapping-tank-body">
                              <div className="stock-mapping-tank-liquid">
                                <div className="stock-mapping-tank-wave stock-mapping-tank-wave-back" />
                                <div className="stock-mapping-tank-wave stock-mapping-tank-wave-front" />
                              </div>
                              <div className="stock-mapping-tank-reflection" />
                              <span className="stock-mapping-tank-name">{tank.tank_name || `Tank ${tank.id}`}</span>
                              <span className="stock-mapping-tank-fuel">{tank.fuel_type || 'Fuel'}</span>
                            </div>
                          </div>
                        </div>

                        <div className="stock-mapping-target-chips">
                          {tank.mappedNozzles.length ? (
                            tank.mappedNozzles.map((nozzle) => (
                              <span key={nozzle.id} className="stock-mapping-target-chip">
                                {nozzle.name}
                              </span>
                            ))
                          ) : (
                            <span className="stock-mapping-target-empty">No nozzle mapped yet</span>
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>

      <section className="stock-grid two-up">
        <div className="stock-card">
          <div className="stock-card-head">
            <div>
              <span>Delivery Receipt</span>
              <h3>Add tank load receipt</h3>
            </div>
          </div>

          <div className="stock-form-grid">
            <label>
              <span>Tank</span>
              <select value={deliveryForm.tank_id} onChange={(e) => setDeliveryForm({ ...deliveryForm, tank_id: e.target.value })}>
                <option value="">Select tank</option>
                {tanks.map((tank) => (
                  <option key={tank.id} value={tank.id}>{tank.tank_name || tank.fuel_type}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Delivery date</span>
              <input type="datetime-local" value={deliveryForm.delivery_date} onChange={(e) => setDeliveryForm({ ...deliveryForm, delivery_date: e.target.value })} />
            </label>
            <label>
              <span>Invoice no</span>
              <input value={deliveryForm.invoice_no} onChange={(e) => setDeliveryForm({ ...deliveryForm, invoice_no: e.target.value })} />
            </label>
            <label>
              <span>Supplier</span>
              <input value={deliveryForm.supplier_name} onChange={(e) => setDeliveryForm({ ...deliveryForm, supplier_name: e.target.value })} />
            </label>
            <label>
              <span>Receipt litres</span>
              <input type="number" value={deliveryForm.receipt_litres} onChange={(e) => setDeliveryForm({ ...deliveryForm, receipt_litres: e.target.value })} />
            </label>
          </div>

          <label className="stock-full-width">
            <span>Remarks</span>
            <textarea rows="3" value={deliveryForm.remarks} onChange={(e) => setDeliveryForm({ ...deliveryForm, remarks: e.target.value })} />
          </label>

          <div className="stock-actions-row">
            <button type="button" className="stock-btn primary" onClick={saveDelivery} disabled={saving}>
              {saving ? 'Saving...' : 'Save Receipt'}
            </button>
          </div>
        </div>

        <div className="stock-card">
          <div className="stock-card-head">
            <div>
              <span>Dip Chart Preview</span>
              <h3>{selectedTank ? `${selectedTank.tank_name || selectedTank.fuel_type} chart` : 'Select a tank'}</h3>
            </div>
            {selectedTank && HPCL_STANDARD_CAPACITIES.some((item) => Math.abs(item - Number(selectedTank.tank_capacity || 0)) <= 1) && (
              <div className="stock-fuel-badge diesel">ISI Standard</div>
            )}
          </div>

          {dipChartSeries.length > 0 && (
            <div className="stock-chart-shell">
              <div className="stock-chart-headline">
                <strong>Physical volume curve</strong>
                <span>Dip depth vs tank volume for the selected tank</span>
              </div>
              <div className="stock-chart-panel">
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart
                    data={dipChartSeries}
                    margin={{ top: 12, right: 16, left: 4, bottom: 8 }}
                  >
                    <defs>
                      <linearGradient id="stockDipVolumeFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={dipChartColor} stopOpacity={0.34} />
                        <stop offset="95%" stopColor={dipChartColor} stopOpacity={0.04} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.35)" />
                    <XAxis
                      dataKey="dip"
                      tick={{ fill: '#475467', fontSize: 12 }}
                      tickLine={false}
                      axisLine={false}
                      unit=" cm"
                    />
                    <YAxis
                      tick={{ fill: '#475467', fontSize: 12 }}
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => wholeQty(value)}
                    />
                    <Tooltip
                      formatter={(value, name) => [
                        name === 'physical' ? `${wholeQty(value)} L` : `${qty(value)} L`,
                        name === 'physical' ? 'Physical volume' : 'Diff / mm',
                      ]}
                      labelFormatter={(value) => `Dip ${value} cm`}
                      contentStyle={{
                        borderRadius: 12,
                        border: '1px solid rgba(148, 163, 184, 0.2)',
                        boxShadow: '0 16px 40px rgba(15, 23, 42, 0.12)',
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="physical"
                      stroke={dipChartColor}
                      strokeWidth={3}
                      fill="url(#stockDipVolumeFill)"
                      dot={false}
                      activeDot={{ r: 5, strokeWidth: 0 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div className="stock-table-wrap short">
            <table className="stock-table">
              <thead>
                <tr>
                  <th>Dip (cm)</th>
                  <th>Physical (L)</th>
                  <th>Diff / mm</th>
                </tr>
              </thead>
              <tbody>
                {dipChartRows.slice(0, 40).map((row) => (
                  <tr key={row.dip_cm}>
                    <td>{row.dip_cm}</td>
                    <td>{wholeQty(row.physical_volume_litres)}</td>
                    <td>{qty(row.diff_litres_per_mm)}</td>
                  </tr>
                ))}
                {!dipChartRows.length && (
                  <tr>
                    <td colSpan="3">Select a tank to preview the generated dip chart.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="stock-grid two-up">
        <div className="stock-card">
          <div className="stock-card-head">
            <div>
              <span>Manual Totalizer</span>
              <h3>Enter nozzle-wise meter movement</h3>
            </div>
          </div>

          <div className="stock-form-grid">
            <label>
              <span>Tank</span>
              <select
                value={manualTotalizerForm.tank_id}
                onChange={(e) =>
                  setManualTotalizerForm((prev) => ({
                    ...prev,
                    tank_id: e.target.value,
                    nozzle_id: '',
                    opening_totalizer: '',
                    closing_totalizer: '',
                    testing_litres: '',
                    rate: '',
                  }))
                }
              >
                <option value="">Select tank</option>
                {manualAvailableTanks.map((tank) => (
                  <option key={tank.id} value={tank.id}>
                    {tank.tank_name || tank.fuel_type} - {tank.fuel_type}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Associated nozzle</span>
              <select
                value={manualTotalizerForm.nozzle_id}
                onChange={(e) => setManualTotalizerForm((prev) => ({ ...prev, nozzle_id: e.target.value }))}
                disabled={!manualTotalizerForm.tank_id}
              >
                <option value="">
                  {manualTotalizerForm.tank_id ? 'Select associated nozzle' : 'Select tank first'}
                </option>
                {manualTankNozzles.map((nozzle) => (
                  <option key={nozzle.id} value={nozzle.id}>
                    {nozzle.pump_name ? `${nozzle.pump_name} - ` : ''}
                    {nozzle.name} - {nozzle.fuel_type}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Entry date &amp; time</span>
              <input
                type="datetime-local"
                value={manualTotalizerForm.entry_datetime}
                onChange={(e) => setManualTotalizerForm((prev) => ({ ...prev, entry_datetime: e.target.value }))}
              />
            </label>
            <label>
              <span>Opening totalizer</span>
              <input
                type="number"
                step="0.001"
                value={manualTotalizerForm.opening_totalizer}
                readOnly={manualOpeningLocked}
                disabled={!manualTotalizerForm.nozzle_id}
                onChange={(e) => setManualTotalizerForm((prev) => ({ ...prev, opening_totalizer: e.target.value }))}
              />
            </label>
            <label>
              <span>Closing totalizer</span>
              <input
                type="number"
                step="0.001"
                value={manualTotalizerForm.closing_totalizer}
                disabled={!manualTotalizerForm.nozzle_id}
                onChange={(e) => setManualTotalizerForm((prev) => ({ ...prev, closing_totalizer: e.target.value }))}
              />
            </label>
            <label>
              <span>Testing litres</span>
              <input
                type="number"
                step="0.001"
                value={manualTotalizerForm.testing_litres}
                disabled={!manualTotalizerForm.nozzle_id}
                onChange={(e) => setManualTotalizerForm((prev) => ({ ...prev, testing_litres: e.target.value }))}
              />
            </label>
            <label>
              <span>Rate</span>
              <input
                type="number"
                step="0.01"
                value={manualTotalizerForm.rate}
                disabled={!manualTotalizerForm.nozzle_id}
                onChange={(e) => setManualTotalizerForm((prev) => ({ ...prev, rate: e.target.value }))}
              />
            </label>
          </div>

          {selectedManualTank && !manualTankNozzles.length && (
            <div className="stock-inline-empty">
              No mapped nozzles found for this tank yet. Map the nozzle to this tank first.
            </div>
          )}

          {selectedManualTank && manualTankNozzles.length > 0 && !selectedManualNozzle && (
            <div className="stock-inline-empty stock-inline-info">
              {manualTankNozzles.length} associated nozzle{manualTankNozzles.length > 1 ? 's are' : ' is'} ready for{' '}
              {selectedManualTank.tank_name || selectedManualTank.fuel_type}. Select one to load the opening totalizer.
            </div>
          )}

          {selectedManualNozzle && (
            <div className={`stock-inline-empty ${manualOpeningLocked ? 'stock-inline-info' : ''}`}>
              {manualOpeningLocked
                ? `Opening totalizer auto-picked from previous manual closing reading: ${selectedManualNozzle.latest_closing_totalizer}`
                : 'No previous manual closing reading found for this nozzle yet. Enter the first opening totalizer manually.'}
            </div>
          )}

          <div className="stock-inline-metric">
            <div>
              <span>Gross sales</span>
              <strong>{qty(manualDerived.grossLitres)} L</strong>
            </div>
            <div>
              <span>Net sales</span>
              <strong>{qty(manualDerived.netLitres)} L</strong>
            </div>
            <div>
              <span>Net amount</span>
              <strong>{money(manualDerived.netAmount)}</strong>
            </div>
          </div>

          <label className="stock-full-width">
            <span>Remarks</span>
            <textarea
              rows="3"
              value={manualTotalizerForm.remarks}
              onChange={(e) => setManualTotalizerForm((prev) => ({ ...prev, remarks: e.target.value }))}
            />
          </label>

          <div className="stock-actions-row">
            <button type="button" className="stock-btn primary" onClick={saveManualTotalizer} disabled={saving}>
              {saving ? 'Saving...' : 'Save Manual Totalizer'}
            </button>
          </div>
        </div>

        <div className="stock-card">
          <div className="stock-card-head">
            <div>
              <span>Recent Totalizer</span>
              <h3>Latest nozzle-wise manual entries</h3>
            </div>
          </div>

          <div className="stock-table-wrap medium">
            <table className="stock-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Nozzle</th>
                  <th>Tank</th>
                  <th>Gross</th>
                  <th>Net</th>
                </tr>
              </thead>
              <tbody>
                {manualTotalizerEntries.slice(0, 12).map((row) => (
                  <tr key={row.id}>
                    <td>{toDateTimeLocal(row.entry_datetime).replace('T', ' ')}</td>
                    <td>{row.nozzle_name}</td>
                    <td>{row.tank_name}</td>
                    <td>{qty(row.gross_sales_litres)} L</td>
                    <td>{qty(row.net_sales_litres)} L</td>
                  </tr>
                ))}
                {!manualTotalizerEntries.length && (
                  <tr>
                    <td colSpan="5">No manual totalizer entries found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="stock-card">
        <div className="stock-card-head">
          <div>
            <span>Reporting Window</span>
            <h3>Manual totalizer and stock filters</h3>
          </div>
        </div>

        <div className="stock-form-grid">
          <label>
            <span>Tank</span>
            <select value={filters.tank_id} onChange={(e) => setFilters({ ...filters, tank_id: e.target.value })}>
              <option value="">All tanks</option>
              {tanks.map((tank) => (
                <option key={tank.id} value={tank.id}>{tank.tank_name || tank.fuel_type}</option>
              ))}
            </select>
          </label>
          <label>
            <span>Start date</span>
            <input type="date" value={filters.startDate} onChange={(e) => setFilters({ ...filters, startDate: e.target.value })} />
          </label>
          <label>
            <span>End date</span>
            <input type="date" value={filters.endDate} onChange={(e) => setFilters({ ...filters, endDate: e.target.value })} />
          </label>
        </div>
      </section>

      <section className="stock-card">
        <div className="stock-card-head">
          <div>
            <span>Totalizer</span>
            <h3>Daily manual totalizer board</h3>
          </div>
        </div>

        <div className="stock-table-wrap">
          <table className="stock-table wide">
            <thead>
              <tr>
                {(totalizer.columns || []).map((column) => (
                  <th key={column.key}>{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(totalizer.rows || []).map((row, index) => (
                <tr key={`${row.date}-${index}`}>
                  {(totalizer.columns || []).map((column) => (
                    <td key={column.key}>
                      {column.key === 'date' ? row[column.key] : qty(row[column.key])}
                    </td>
                  ))}
                </tr>
              ))}
              {!totalizer.rows?.length && (
                <tr>
                  <td colSpan={Math.max(1, totalizer.columns?.length || 1)}>
                    {loading ? 'Loading totalizer...' : 'No totalizer rows found for this range.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="stock-card">
        <div className="stock-card-head">
          <div>
            <span>DSR</span>
            <h3>Daily stock reconciliation by tank</h3>
          </div>
          <div className="stock-head-actions">
            <button
              type="button"
              className="stock-btn ghost"
              onClick={exportDsrExcel}
              disabled={!dsrRows.length}
            >
              Export Excel
            </button>
            <button
              type="button"
              className="stock-btn ghost"
              onClick={exportDsrPdf}
              disabled={!dsrRows.length}
            >
              Export PDF
            </button>
          </div>
        </div>

        <div className="stock-form-grid stock-section-filter-grid">
          <label>
            <span>Tank</span>
            <select value={dsrTankId} onChange={(e) => setDsrTankId(e.target.value)}>
              <option value="">Select tank</option>
              {tanks.map((tank) => (
                <option key={tank.id} value={tank.id}>
                  {tank.tank_name || tank.fuel_type}
                </option>
              ))}
            </select>
          </label>
          <div className="stock-filter-note">
            <span>Current DSR range</span>
            <strong>{filters.startDate} to {filters.endDate}</strong>
          </div>
        </div>

        <div className="stock-table-wrap">
          <table className="stock-table wide">
            <thead>
              <tr>
                <th>Date</th>
                <th>Tank</th>
                <th>Fuel</th>
                <th>Opening Stock</th>
                <th>Receipt</th>
                <th>Total Stock</th>
                <th>Sales By Meter</th>
                <th>Pump Test</th>
                <th>Net Sales By Meter</th>
                <th>Cumulative Sales</th>
                <th>Sales By Dip</th>
                <th>Daily Variation</th>
                <th>Cumulative Variation</th>
                <th>Closing Stock</th>
              </tr>
            </thead>
            <tbody>
              {dsrRows.map((row, index) => (
                <tr key={`${row.tank_id}-${row.date}-${index}`}>
                  <td>{row.date}</td>
                  <td>{row.tank_name}</td>
                  <td>{row.fuel_type}</td>
                  <td>{qty(row.opening_stock)}</td>
                  <td>{qty(row.receipt)}</td>
                  <td>{qty(row.total_stock)}</td>
                  <td>{qty(row.sales_by_meter)}</td>
                  <td>{qty(row.pump_test)}</td>
                  <td>{qty(row.net_sales_by_meter)}</td>
                  <td>{qty(row.cumulative_sales)}</td>
                  <td>{row.sales_by_dip == null ? '-' : qty(row.sales_by_dip)}</td>
                  <td>{row.variation_daily == null ? '-' : qty(row.variation_daily)}</td>
                  <td>{row.cumulative_variation == null ? '-' : qty(row.cumulative_variation)}</td>
                  <td>{row.closing_stock == null ? '-' : qty(row.closing_stock)}</td>
                </tr>
              ))}
              {!dsrRows.length && (
                <tr>
                  <td colSpan="14">{loading ? 'Loading DSR...' : 'No DSR rows found for this range.'}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="stock-grid two-up">
        <div className="stock-card">
          <div className="stock-card-head">
            <div>
              <span>Recent Dips</span>
              <h3>Latest physical entries</h3>
            </div>
          </div>
          <div className="stock-table-wrap medium">
            <table className="stock-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Tank</th>
                  <th>Dip</th>
                  <th>Physical</th>
                </tr>
              </thead>
              <tbody>
                {dipEntries.slice(0, 12).map((row) => (
                  <tr key={row.id}>
                    <td>{toDateTimeLocal(row.recorded_at).replace('T', ' ')}</td>
                    <td>{row.tank_name}</td>
                    <td>{qty(row.dip_cm)} cm</td>
                    <td>{wholeQty(row.physical_volume_litres)} L</td>
                  </tr>
                ))}
                {!dipEntries.length && (
                  <tr>
                    <td colSpan="4">No dip entries found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="stock-card">
          <div className="stock-card-head">
            <div>
              <span>Recent Receipts</span>
              <h3>Latest stock deliveries</h3>
            </div>
          </div>
          <div className="stock-table-wrap medium">
            <table className="stock-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Tank</th>
                  <th>Invoice</th>
                  <th>Receipt</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.slice(0, 12).map((row) => (
                  <tr key={row.id}>
                    <td>{toDateTimeLocal(row.delivery_date).replace('T', ' ')}</td>
                    <td>{row.tank_name}</td>
                    <td>{row.invoice_no || '-'}</td>
                    <td>{qty(row.receipt_litres)} L</td>
                  </tr>
                ))}
                {!deliveries.length && (
                  <tr>
                    <td colSpan="4">No delivery rows found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
